import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing } from './tasks-help.shared';

/**
 * ?-Hilfe fuer die Routinen-Seite (`/tasks/routines`).
 *
 * Die eine Aussage, an der hier alles haengt: **eine Routine ist eine Vorlage,
 * keine Aufgabe.** Die echten Aufgaben entstehen im naechtlichen Lauf und
 * erscheinen erst zu ihrer "Sichtbar ab"-Zeit in der Uebersicht — deshalb
 * findet man eine gerade angelegte Routine dort nicht sofort wieder. Das ist
 * die haeufigste Rueckfrage zu dieser Seite und traegt die Schritte 1 und 4.
 *
 * Belege im Code (fuer spaetere Reviews):
 *  - Vorlagen-Charakter + Rhythmus-Spalten: `routine-management-list.component.html`
 *  - "erscheint morgen 06:00": `nextAppearance()` ebenda
 *  - Zeilenmenue strikt aus `RoutineAllowedAction`: `hasRowActions()`
 *
 * Seiten-Tour, also KEIN `dialogContext`. `launchContext.route` erlaubt den
 * Start aus dem Hilfe-Hub; der ?-Knopf im Seitenkopf startet sie direkt.
 */
export const TASK_ROUTINES_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskRoutinesHelp,
  version: 1,
  title: 'TASK_ROUTINES_HELP_TOUR_TITLE',
  description: 'TASK_ROUTINES_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: {
    route: '/tasks/routines',
    mode: 'auto',
  },
  triggerContext: {
    route: '/tasks/routines',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 100,
  category: 'feature-discovery',
  // Schritte 2, 4 und 5 haengen an Bestand bzw. Rechten. Ohne Umnummerierung
  // zaehlte das Popover "1 / 6 → 3 / 6" und der Nutzer sucht den fehlenden.
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_ROUTINES_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'rt-welcome',
          anchor: AnchorId.TasksRoutinesPage,
          spotlightAnchor: AnchorId.TasksRoutinesPage,
          title: 'TUTORIAL.TASK_ROUTINES.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_ROUTINES.WELCOME.CONTENT',
          audioStepId: 'rt-welcome-v2',
          placement: 'bottom',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Nur mit `ManageRoutines` im DOM — wer die Seite nur lesen darf,
          // sieht den Knopf gar nicht.
          id: 'rt-create',
          anchor: AnchorId.TasksRoutineCreateButton,
          spotlightAnchor: AnchorId.TasksRoutineCreateButton,
          title: 'TUTORIAL.TASK_ROUTINES.CREATE.TITLE',
          content: 'TUTORIAL.TASK_ROUTINES.CREATE.CONTENT',
          audioStepId: 'rt-create-v2',
          placement: 'bottom',
          align: 'end',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksRoutineCreateButton),
        },
        {
          id: 'rt-filter',
          anchor: AnchorId.TasksRoutinesFilter,
          spotlightAnchor: AnchorId.TasksRoutinesFilter,
          title: 'TUTORIAL.TASK_ROUTINES.FILTER.TITLE',
          content: 'TUTORIAL.TASK_ROUTINES.FILTER.CONTENT',
          audioStepId: 'rt-filter-v2',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Kern-Step: Rhythmus und "Naechste Ausfuehrung" erklaeren, warum eine
          // Routine nicht sofort als Aufgabe auftaucht. `-v3` seit 10.10.2026 (der Klick auf
          // die Erfuellung oeffnet die „Nachweise“, nicht mehr die „Compliance“), bis zur
          // Vertonung stumm.
          id: 'rt-table',
          anchor: AnchorId.TasksRoutinesTable,
          spotlightAnchor: AnchorId.TasksRoutinesTable,
          title: 'TUTORIAL.TASK_ROUTINES.TABLE.TITLE',
          content: 'TUTORIAL.TASK_ROUTINES.TABLE.CONTENT',
          audioStepId: 'rt-table-v3',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksRoutinesTable),
        },
        {
          id: 'rt-row-menu',
          anchor: AnchorId.TasksRoutineRowMenu,
          spotlightAnchor: AnchorId.TasksRoutineRowMenu,
          title: 'TUTORIAL.TASK_ROUTINES.ROW_MENU.TITLE',
          content: 'TUTORIAL.TASK_ROUTINES.ROW_MENU.CONTENT',
          audioStepId: 'rt-row-menu-v2',
          placement: 'left',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksRoutineRowMenu),
        },
        {
          // Ueberleitung: „Nachweise“ (Unterseite von „Lage“, frueher Reiter „Compliance“)
          // wertet in Quote, Tabelle und Tagesnachweis AUSSCHLIESSLICH Routinen aus — das
          // gehoert an genau diese Stelle, weil hier die Vorlagen leben. `-v3` seit 10.10.2026
          // (Text nennt „Nachweise“ statt „Compliance“), bis zur Vertonung stumm.
          id: 'rt-to-compliance',
          anchor: AnchorId.TasksTabs,
          spotlightAnchor: AnchorId.TasksTabs,
          title: 'TUTORIAL.TASK_ROUTINES.TO_COMPLIANCE.TITLE',
          content: 'TUTORIAL.TASK_ROUTINES.TO_COMPLIANCE.CONTENT',
          audioStepId: 'rt-to-compliance-v3',
          placement: 'bottom',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          actions: [
            {
              labelKey: 'TUTORIAL.TASK_ROUTINES.TO_COMPLIANCE.ACTION',
              icon: 'fa-light fa-clipboard-check',
              route: '/tasks/lage/nachweise',
              startTour: TourFeature.TaskNachweiseHelp,
              variant: 'primary',
            },
          ],
        },
      ],
    },
  ],
};
